import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faLayerGroup, faTimes, faPlus, faPen, faTrash, faCheck, faFile } from '@fortawesome/free-solid-svg-icons';

import { ButtonComponent } from '../button/button.component';
import { ModalDeleteConfirmComponent } from '../modal-delete-confirm/modal-delete-confirm.component';
import { Archive } from '../../services/archives.service';
import { TagsService } from '../../services/tags.service';
import { FileGroupsService, FileGroup } from '../../services/file-groups.service';
import { AlertService } from '../alert-service/alert-service';

interface PredefinedColor {
  name: string;
  bg_color: string;
  text_color: string;
}

@Component({
  selector: 'app-modal-file-groups',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule, ButtonComponent, ModalDeleteConfirmComponent],
  templateUrl: './modal-file-groups.component.html',
  styleUrls: ['./modal-file-groups.component.scss']
})
export class ModalFileGroupsComponent implements OnChanges {
  @Input() show = false;
  @Input() interactionId: string = '';
  @Input() archives: Archive[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() groupsChanged = new EventEmitter<FileGroup[]>();

  faLayerGroup = faLayerGroup;
  faTimes = faTimes;
  faPlus = faPlus;
  faPen = faPen;
  faTrash = faTrash;
  faCheck = faCheck;
  faFile = faFile;

  groups: FileGroup[] = [];
  isLoading = false;
  predefinedColors: PredefinedColor[] = [];

  showNewGroupForm = false;
  newGroupName = '';
  newGroupColor: PredefinedColor | null = null;

  editingGroupId: string | null = null;
  editGroupName = '';
  editGroupColor: PredefinedColor | null = null;

  showDeleteConfirm = false;
  groupToDelete: FileGroup | null = null;

  constructor(
    private fileGroupsService: FileGroupsService,
    private tagsService: TagsService,
    private alertService: AlertService,
    private translate: TranslateService
  ) {
    this.predefinedColors = this.tagsService.getPredefinedColors();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['show'] && this.show && this.interactionId) {
      this.resetForms();
      this.loadGroups();
    }
  }

  private resetForms() {
    this.showNewGroupForm = false;
    this.newGroupName = '';
    this.newGroupColor = null;
    this.editingGroupId = null;
    this.editGroupName = '';
    this.editGroupColor = null;
    this.showDeleteConfirm = false;
    this.groupToDelete = null;
  }

  loadGroups() {
    this.isLoading = true;
    this.fileGroupsService.list(this.interactionId).subscribe({
      next: (groups) => {
        this.groups = groups;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.alertService.showError(err);
      }
    });
  }

  openNewGroupForm() {
    this.showNewGroupForm = true;
    this.newGroupName = '';
    this.newGroupColor = this.predefinedColors[(this.groups.length) % this.predefinedColors.length] || this.predefinedColors[0];
  }

  cancelNewGroupForm() {
    this.showNewGroupForm = false;
    this.newGroupName = '';
    this.newGroupColor = null;
  }

  createGroup() {
    const name = this.newGroupName.trim();
    if (!name || !this.newGroupColor) {
      return;
    }

    this.fileGroupsService.create(this.interactionId, {
      name,
      text_color: this.newGroupColor.text_color,
      bg_color: this.newGroupColor.bg_color,
      archive_ids: []
    }).subscribe({
      next: (group) => {
        this.groups = [...this.groups, group];
        this.cancelNewGroupForm();
      },
      error: (err) => {
        this.alertService.showError(err);
      }
    });
  }

  startEditGroup(group: FileGroup) {
    this.editingGroupId = group.id;
    this.editGroupName = group.name;
    this.editGroupColor = this.predefinedColors.find(c => c.bg_color === group.bg_color) || this.predefinedColors[0];
  }

  cancelEditGroup() {
    this.editingGroupId = null;
    this.editGroupName = '';
    this.editGroupColor = null;
  }

  saveEditGroup(group: FileGroup) {
    const name = this.editGroupName.trim();
    if (!name || !this.editGroupColor) {
      return;
    }

    this.fileGroupsService.update(this.interactionId, group.id, {
      name,
      text_color: this.editGroupColor.text_color,
      bg_color: this.editGroupColor.bg_color
    }).subscribe({
      next: (updated) => {
        this.groups = this.groups.map(g => g.id === updated.id ? updated : g);
        this.cancelEditGroup();
      },
      error: (err) => {
        this.alertService.showError(err);
      }
    });
  }

  requestDeleteGroup(group: FileGroup) {
    this.groupToDelete = group;
    this.showDeleteConfirm = true;
  }

  onDeleteConfirmClose() {
    this.showDeleteConfirm = false;
    this.groupToDelete = null;
  }

  onDeleteConfirmConfirm() {
    const group = this.groupToDelete;
    if (!group) {
      this.onDeleteConfirmClose();
      return;
    }

    this.fileGroupsService.delete(this.interactionId, group.id).subscribe({
      next: () => {
        this.groups = this.groups.filter(g => g.id !== group.id);
        this.onDeleteConfirmClose();
      },
      error: (err) => {
        this.alertService.showError(err);
        this.onDeleteConfirmClose();
      }
    });
  }

  isArchiveInGroup(group: FileGroup, archiveId: string | number): boolean {
    return (group.archive_ids || []).some(id => String(id) === String(archiveId));
  }

  toggleArchiveInGroup(group: FileGroup, archiveId: string | number) {
    const idStr = String(archiveId);
    const current = group.archive_ids || [];
    const newIds = this.isArchiveInGroup(group, archiveId)
      ? current.filter(id => String(id) !== idStr)
      : [...current, idStr];

    this.fileGroupsService.updateArchives(this.interactionId, group.id, newIds).subscribe({
      next: (updated) => {
        this.groups = this.groups.map(g => g.id === updated.id ? updated : g);
      },
      error: (err) => {
        this.alertService.showError(err);
      }
    });
  }

  onClose() {
    this.groupsChanged.emit(this.groups);
    this.close.emit();
  }

  get deleteGroupImpactItems(): string[] {
    if (!this.groupToDelete) return [];
    const count = (this.groupToDelete.archive_ids || []).length;
    return count > 0 ? [this.translate.instant('FILE_GROUPS.DELETE_IMPACT', { count })] : [];
  }
}
